import {computed, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {LatitudeLongitude} from '../../../apiclients/playble';

export const BRISBANE_CBD_LATITUDE = -27.4705;
export const BRISBANE_CBD_LONGITUDE = 153.0260;
export const MAX_USABLE_ACCURACY_METERS = 10_000;
export const GEO_SESSION_KEY = 'playble.geo.v1';
export const GEO_SESSION_MAX_AGE_MS = 15 * 60 * 1000;

/** Generous South East Queensland bounds: Noosa to Coolangatta, Toowoomba to the coast. */
export const SEQ_SERVICE_AREA = {
  minLatitude: -28.35,
  maxLatitude: -26.15,
  minLongitude: 151.65,
  maxLongitude: 153.55,
} as const;

export type GeolocationStatus =
  | 'idle'
  | 'requesting'
  | 'granted'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  | 'error'
  | 'fallback'
  | 'imprecise'
  | 'outside-region';

export type GeolocationRequestMode = 'coarse' | 'precise';

export interface UserCoordinates {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  timestamp: number | null;
  source: 'gps' | 'fallback';
}

export interface GeolocationEnvironment {
  isIos: boolean;
  isStandalone: boolean;
  isInAppBrowser: boolean;
  inAppBrowserName: string | null;
  isSecureContext: boolean;
  geolocationSupported: boolean;
}

export interface LocationBannerView {
  status: GeolocationStatus;
  showBanner: boolean;
  tone: 'success' | 'info' | 'warning' | 'danger';
  icon: string;
  title: string;
  message: string;
  helpText: string | null;
  ctaLabel: string | null;
  canRetry: boolean;
  preferPreciseRetry: boolean;
  showBrowseDefault: boolean;
}

export type EmptyCatalogHint = 'ok' | 'outside-region' | 'date';

interface StoredCoordinates {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  timestamp: number;
}

const COARSE_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 8000,
  maximumAge: 60_000,
};

const PRECISE_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

export function brisbaneFallbackCoordinates(): UserCoordinates {
  return {
    latitude: BRISBANE_CBD_LATITUDE,
    longitude: BRISBANE_CBD_LONGITUDE,
    accuracyMeters: null,
    timestamp: null,
    source: 'fallback',
  };
}

export function isInSeqServiceArea(latitude: number, longitude: number): boolean {
  return latitude >= SEQ_SERVICE_AREA.minLatitude
    && latitude <= SEQ_SERVICE_AREA.maxLatitude
    && longitude >= SEQ_SERVICE_AREA.minLongitude
    && longitude <= SEQ_SERVICE_AREA.maxLongitude;
}

export function isAccuracyUsable(accuracyMeters: number | null | undefined): boolean {
  return typeof accuracyMeters === 'number'
    && Number.isFinite(accuracyMeters)
    && accuracyMeters <= MAX_USABLE_ACCURACY_METERS;
}

export function detectIos(userAgent: string, platform?: string, maxTouchPoints?: number): boolean {
  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return true;
  }
  return platform === 'MacIntel' && (maxTouchPoints ?? 0) > 1;
}

export function detectStandalone(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches === true;
  const iosStandalone = (navigator as Navigator & {standalone?: boolean}).standalone === true;
  return displayModeStandalone || iosStandalone;
}

export function detectInAppBrowser(userAgent: string): string | null {
  const ua = userAgent ?? '';
  if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(ua)) {
    return 'Facebook';
  }
  if (/Instagram/i.test(ua)) {
    return 'Instagram';
  }
  if (/\bMessenger\b/i.test(ua)) {
    return 'Messenger';
  }
  if (/TikTok|BytedanceWebview|musical_ly/i.test(ua)) {
    return 'TikTok';
  }
  if (/Line\//i.test(ua)) {
    return 'LINE';
  }
  if (/Snapchat/i.test(ua)) {
    return 'Snapchat';
  }
  if (/WhatsApp/i.test(ua)) {
    return 'WhatsApp';
  }
  return null;
}

export function classifyGeolocationError(error: unknown): Extract<GeolocationStatus, 'denied' | 'unavailable' | 'timeout' | 'error'> {
  const code = error && typeof error === 'object' && 'code' in error
    ? Number((error as {code?: number}).code)
    : NaN;

  switch (code) {
    case 1:
      return 'denied';
    case 2:
      return 'unavailable';
    case 3:
      return 'timeout';
    default:
      return 'error';
  }
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private platformId = inject(PLATFORM_ID);
  private lastGoodGps: UserCoordinates | null = null;
  private nextRequestMode: GeolocationRequestMode = 'coarse';
  private requestInFlight: Promise<void> | null = null;
  private permissionListenerBound = false;

  readonly status = signal<GeolocationStatus>('fallback');
  readonly coordinates = signal<UserCoordinates>(brisbaneFallbackCoordinates());
  readonly environment = signal<GeolocationEnvironment>({
    isIos: false,
    isStandalone: false,
    isInAppBrowser: false,
    inAppBrowserName: null,
    isSecureContext: true,
    geolocationSupported: false,
  });

  readonly queryPosition = computed<LatitudeLongitude>(() => ({
    latitude: this.coordinates().latitude,
    longitude: this.coordinates().longitude,
  }));

  readonly isRequesting = computed(() => this.status() === 'requesting');
  readonly locationWorking = computed(() => this.status() === 'granted');
  readonly hasGpsFix = computed(() => {
    const status = this.status();
    return status === 'granted' || status === 'outside-region';
  });
  readonly usingFallback = computed(() => this.coordinates().source === 'fallback');
  readonly ctaLabel = computed(() => this.hasGpsFix() ? 'Update my location' : 'Use my location');
  readonly view = computed(() => this.buildView());

  emptyCatalogHint(hasResults: boolean): EmptyCatalogHint {
    if (hasResults) {
      return 'ok';
    }
    if (this.status() === 'outside-region' && this.coordinates().source === 'gps') {
      return 'outside-region';
    }
    return 'date';
  }

  async initialize(options?: {autoPrompt?: boolean}): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.coordinates.set(brisbaneFallbackCoordinates());
      this.status.set('fallback');
      return;
    }

    this.captureEnvironment();
    this.restoreSessionIfUsable();

    const env = this.environment();
    if (!env.geolocationSupported || !env.isSecureContext) {
      this.applyFailure('unavailable');
      return;
    }

    if (env.isInAppBrowser && !options?.autoPrompt) {
      if (this.status() !== 'granted') {
        this.applyFailure('unavailable');
      }
    }

    const permission = await this.readPermissionState();
    if (permission === 'granted') {
      await this.requestPosition('coarse');
      return;
    }

    if (permission === 'denied') {
      // Permissions API is not always trustworthy (Safari, iOS PWA, and some
      // Chromium states). Keep a recent GPS fix instead of looking like failure.
      if (this.status() !== 'granted' && !this.lastGoodGps) {
        this.applyFailure('denied');
      }
      return;
    }

    const canAutoPrompt = !!options?.autoPrompt && this.canAutoPrompt();
    if (canAutoPrompt) {
      await this.requestPosition('coarse');
    } else if (this.status() === 'idle') {
      this.status.set('fallback');
    }
  }

  async requestFromUserGesture(forcePrecise = false): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.captureEnvironment();
    const env = this.environment();
    if (!env.geolocationSupported || !env.isSecureContext) {
      this.applyFailure('unavailable');
      return;
    }

    const mode: GeolocationRequestMode = forcePrecise || this.nextRequestMode === 'precise'
      ? 'precise'
      : 'coarse';
    await this.requestPosition(mode);
  }

  browseDefaultRegion(): void {
    this.lastGoodGps = null;
    this.coordinates.set(brisbaneFallbackCoordinates());
    this.status.set('fallback');
    this.nextRequestMode = 'coarse';
  }

  requestRawPosition(mode: GeolocationRequestMode = 'precise'): Promise<GeolocationPosition> {
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      return Promise.reject({code: 2, message: 'Geolocation is unavailable'});
    }
    return this.getCurrentPosition(mode === 'precise' ? PRECISE_OPTIONS : COARSE_OPTIONS);
  }

  private canAutoPrompt(): boolean {
    const env = this.environment();
    return !env.isIos && !env.isInAppBrowser && env.geolocationSupported && env.isSecureContext;
  }

  private captureEnvironment(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const userAgent = navigator.userAgent ?? '';
    const inAppBrowserName = detectInAppBrowser(userAgent);
    this.environment.set({
      isIos: detectIos(userAgent, navigator.platform, navigator.maxTouchPoints),
      isStandalone: detectStandalone(),
      isInAppBrowser: inAppBrowserName !== null,
      inAppBrowserName,
      isSecureContext: window.isSecureContext !== false,
      geolocationSupported: 'geolocation' in navigator,
    });
  }

  private restoreSessionIfUsable(): void {
    if (this.lastGoodGps) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(GEO_SESSION_KEY);
      if (!raw) {
        return;
      }
      const stored = JSON.parse(raw) as StoredCoordinates;
      const age = Date.now() - stored.timestamp;
      if (age < 0 || age > GEO_SESSION_MAX_AGE_MS) {
        return;
      }
      if (!isAccuracyUsable(stored.accuracyMeters) || !isInSeqServiceArea(stored.latitude, stored.longitude)) {
        return;
      }

      const restored: UserCoordinates = {
        latitude: stored.latitude,
        longitude: stored.longitude,
        accuracyMeters: stored.accuracyMeters,
        timestamp: stored.timestamp,
        source: 'gps',
      };
      this.lastGoodGps = restored;
      this.coordinates.set(restored);
      this.status.set('granted');
    } catch {
      // Ignore malformed session data.
    }
  }

  private persistSession(coords: UserCoordinates): void {
    if (!isPlatformBrowser(this.platformId) || coords.source !== 'gps' || coords.timestamp == null) {
      return;
    }
    try {
      const stored: StoredCoordinates = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracyMeters: coords.accuracyMeters,
        timestamp: coords.timestamp,
      };
      sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify(stored));
    } catch {
      // Private mode can throw.
    }
  }

  private async readPermissionState(): Promise<PermissionState | 'unsupported'> {
    try {
      if (!navigator.permissions?.query) {
        return 'unsupported';
      }
      const result = await navigator.permissions.query({name: 'geolocation'} as PermissionDescriptor);
      if (!this.permissionListenerBound) {
        this.permissionListenerBound = true;
        result.addEventListener('change', () => {
          // Permissions API can be stale, especially after a user-gesture
          // getCurrentPosition. Only auto-upgrade when it reports granted.
          if (result.state === 'granted' && this.status() !== 'requesting' && this.status() !== 'granted') {
            void this.requestPosition('coarse');
          }
        });
      }
      return result.state;
    } catch {
      return 'unsupported';
    }
  }

  private async requestPosition(mode: GeolocationRequestMode): Promise<void> {
    this.status.set('requesting');
    const request = this.runRequest(mode).finally(() => {
      if (this.requestInFlight === request) {
        this.requestInFlight = null;
      }
    });
    this.requestInFlight = request;
    return request;
  }

  private async runRequest(mode: GeolocationRequestMode): Promise<void> {
    try {
      const position = await this.getCurrentPosition(mode === 'precise' ? PRECISE_OPTIONS : COARSE_OPTIONS);
      this.applyPosition(position);
    } catch (error) {
      const classified = classifyGeolocationError(error);
      if (mode === 'coarse' && (classified === 'timeout' || classified === 'unavailable' || classified === 'error')) {
        this.nextRequestMode = 'precise';
      }
      this.applyFailure(classified);
    }
  }

  private applyPosition(position: GeolocationPosition): void {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const accuracyMeters = Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null;
    const timestamp = position.timestamp || Date.now();

    if (!isAccuracyUsable(accuracyMeters)) {
      this.nextRequestMode = 'precise';
      this.status.set('imprecise');
      if (!this.lastGoodGps) {
        this.coordinates.set(brisbaneFallbackCoordinates());
      }
      return;
    }

    const coords: UserCoordinates = {
      latitude,
      longitude,
      accuracyMeters,
      timestamp,
      source: 'gps',
    };

    if (!isInSeqServiceArea(latitude, longitude)) {
      this.lastGoodGps = coords;
      this.coordinates.set(coords);
      this.status.set('outside-region');
      this.nextRequestMode = 'coarse';
      return;
    }

    this.lastGoodGps = coords;
    this.coordinates.set(coords);
    this.status.set('granted');
    this.nextRequestMode = 'coarse';
    this.persistSession(coords);
  }

  private applyFailure(status: Extract<GeolocationStatus, 'denied' | 'unavailable' | 'timeout' | 'error'>): void {
    if (this.lastGoodGps) {
      this.coordinates.set(this.lastGoodGps);
    } else {
      this.coordinates.set(brisbaneFallbackCoordinates());
    }
    this.status.set(status);
  }

  private getCurrentPosition(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({code: 2, message: 'Geolocation is unavailable'});
        return;
      }

      let settled = false;
      const finish = (cb: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(watchdog);
        cb();
      };

      const timeoutMs = (options.timeout ?? 8000) + 500;
      const watchdog = setTimeout(() => {
        finish(() => reject({code: 3, message: 'Geolocation timed out'}));
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        position => finish(() => resolve(position)),
        error => finish(() => reject(error)),
        options
      );
    });
  }

  private buildView(): LocationBannerView {
    const status = this.status();
    const env = this.environment();
    const retryLabel = this.hasGpsFix() ? 'Update my location' : 'Use my location';

    switch (status) {
      case 'requesting':
        return {
          status,
          showBanner: true,
          tone: 'info',
          icon: 'location_searching',
          title: 'Finding your location',
          message: 'This should only take a moment. Distances will update when we have a fix.',
          helpText: null,
          ctaLabel: null,
          canRetry: false,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
      case 'granted':
        return {
          status,
          showBanner: true,
          tone: 'success',
          icon: 'my_location',
          title: 'Using your location',
          message: 'Distances are sorted from where you are now.',
          helpText: null,
          ctaLabel: 'Update my location',
          canRetry: true,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
      case 'fallback':
        return {
          status,
          showBanner: true,
          tone: 'warning',
          icon: 'location_off',
          title: 'Using Brisbane as a default',
          message: 'Enable location for distances near you. We only use it to sort nearby results.',
          helpText: this.iosHelpText(env),
          ctaLabel: retryLabel,
          canRetry: true,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
      case 'denied':
        return {
          status,
          showBanner: true,
          tone: 'danger',
          icon: 'location_disabled',
          title: 'Location permission is off',
          message: 'Showing Brisbane listings until location is allowed.',
          helpText: this.deniedHelpText(env),
          ctaLabel: 'Try again',
          canRetry: true,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
      case 'timeout':
        return {
          status,
          showBanner: true,
          tone: 'warning',
          icon: 'timer_off',
          title: 'Location timed out',
          message: 'We could not get a fix in time. Try again outdoors, or keep browsing Brisbane listings.',
          helpText: this.iosHelpText(env),
          ctaLabel: 'Try again',
          canRetry: true,
          preferPreciseRetry: true,
          showBrowseDefault: false,
        };
      case 'unavailable':
        return {
          status,
          showBanner: true,
          tone: 'warning',
          icon: 'wrong_location',
          title: 'Location is unavailable',
          message: env.inAppBrowserName
            ? `${env.inAppBrowserName}'s in-app browser blocks location, so we are showing Brisbane listings.`
            : 'This browser cannot share location, so we are showing Brisbane listings.',
          helpText: this.unavailableHelpText(env),
          ctaLabel: env.geolocationSupported ? 'Try again' : null,
          canRetry: env.geolocationSupported,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
      case 'error':
        return {
          status,
          showBanner: true,
          tone: 'danger',
          icon: 'error_outline',
          title: 'Could not read your location',
          message: 'Showing Brisbane listings for now. You can retry when you are ready.',
          helpText: this.iosHelpText(env),
          ctaLabel: 'Try again',
          canRetry: true,
          preferPreciseRetry: true,
          showBrowseDefault: false,
        };
      case 'imprecise':
        return {
          status,
          showBanner: true,
          tone: 'warning',
          icon: 'location_searching',
          title: 'Location is too imprecise',
          message: 'That fix was too coarse to sort nearby. Try again outdoors with Precise Location on.',
          helpText: this.iosHelpText(env),
          ctaLabel: 'Try a more precise location',
          canRetry: true,
          preferPreciseRetry: true,
          showBrowseDefault: false,
        };
      case 'outside-region':
        return {
          status,
          showBanner: true,
          tone: 'warning',
          icon: 'public',
          title: 'You are outside Brisbane / SEQ',
          message: 'Playble currently lists events in South East Queensland. Distances are from your current location.',
          helpText: null,
          ctaLabel: 'Update my location',
          canRetry: true,
          preferPreciseRetry: false,
          showBrowseDefault: true,
        };
      default:
        return {
          status,
          showBanner: false,
          tone: 'info',
          icon: 'place',
          title: '',
          message: '',
          helpText: null,
          ctaLabel: null,
          canRetry: false,
          preferPreciseRetry: false,
          showBrowseDefault: false,
        };
    }
  }

  private iosHelpText(env: GeolocationEnvironment): string | null {
    if (!env.isIos) {
      return null;
    }
    if (env.isStandalone) {
      return 'Home Screen Playble has its own Location Services entry. Open Settings → Privacy & Security → Location Services → Playble, allow access, and turn Precise Location on.';
    }
    return 'On iPhone, allow location for Safari under Settings → Privacy & Security → Location Services, and keep Precise Location on.';
  }

  private deniedHelpText(env: GeolocationEnvironment): string {
    return this.iosHelpText(env)
      ?? 'Enable location for this site in your browser settings, then tap Try again.';
  }

  private unavailableHelpText(env: GeolocationEnvironment): string | null {
    if (env.inAppBrowserName) {
      return env.isIos
        ? `Open in Safari: tap the ⋯ or Share menu, then choose Open in Safari.`
        : `Open in your device browser: tap the ⋯ menu, then choose Open in Chrome or Open in browser.`;
    }
    return this.iosHelpText(env);
  }
}
